import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { ApolloQueryResult, useReactiveVar } from "@apollo/client";
import {
    GetRoleForManagementPageQuery,
    useDeleteOrRestoreCustomRoleMutation,
    useGetRoleForManagementPageQuery,
    useGetUsableRoleLazyQuery,
} from "@me-team/host/main/RoleManagement/graphql/roleManagement.hooks";
import { useNavigate, useParams } from "react-router-dom";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import useScrollToTopOnChange from "@me-team/common/src/hooks/useScrollToTopOnChange";

const ROLE_MANAGEMENT_ITEMS_PER_PAGE = 20;

type RoleToDel = {
    roleId: number;
    roleName: string;
    isUsableRole?: boolean;
};

type RoleToClone = {
    roleId: number;
    roleName: string;
};

type RoleManagementReturn = {
    roleData: GetRoleForManagementPageQuery;
    roleDataLoading: boolean;
    refetchRole: () => Promise<ApolloQueryResult<any>>;

    onDeleteRole: (roleInfo: RoleToDel) => void;
    deleteTarget: RoleToDel | null;
    setDeleteTarget: Dispatch<SetStateAction<RoleToDel | null>>;
    onSubmitDeleteHandler: (roleId: number) => Promise<void>;
    isUsableRole: boolean;
    onCloneRole: (roleInfo: RoleToClone) => void;
    cloneTarget: RoleToClone | null;
    setCloneTarget: Dispatch<SetStateAction<RoleToClone | null>>;
    onSubmitCloneHandler: (roleInfo: RoleToClone) => Promise<void>;
    currentPage: number;
    pageCount: number;
    totalItems: number;
    firstItemIndex: number;
    lastItemIndex: number;
    handlePageChange: (pageNumber: number) => void;
};

const useRoleManagement = (): RoleManagementReturn => {
    const currentCompanyId = Number(useReactiveVar(currentCompanyIdVar));
    const { branchId } = useParams();
    const navigate = useNavigate();
    const canDeleteRole = useRoleAccess(CompanyAccess.RoleDelete);
    const canCloneRole = useRoleAccess(CompanyAccess.RoleCreateClone);
    const [currentPage, setCurrentPage] = useState(1);

    useScrollToTopOnChange(currentPage);

    const {
        data: roleData,
        loading: roleDataLoading,
        refetch: refetchRole,
    } = useGetRoleForManagementPageQuery({
        variables: {
            branchId: Number(branchId),
            page: currentPage,
            itemsPerPage: ROLE_MANAGEMENT_ITEMS_PER_PAGE,
        },
        fetchPolicy: "network-only",
        context: {
            errorType: "local",
        },
        skip: !branchId,
    });

    const [getUsableRole] = useGetUsableRoleLazyQuery({
        fetchPolicy: "network-only",
    });

    const [deleteTarget, setDeleteTarget] = useState<RoleToDel | null>(null);
    const [isUsableRole, setIsUsableRol] = useState<boolean>(false);
    const [cloneTarget, setCloneTarget] = useState<RoleToClone | null>(null);
    const totalItems = roleData?.role?.totalCount ?? 0;
    const pageCount = Number(roleData?.role?.pageCount || 1);
    const firstItemIndex = (currentPage - 1) * ROLE_MANAGEMENT_ITEMS_PER_PAGE + 1;
    const lastItemIndex = Math.min(currentPage * ROLE_MANAGEMENT_ITEMS_PER_PAGE, totalItems);

    const handlePageChange = useCallback((pageNumber: number) => {
        setCurrentPage(pageNumber);
    }, []);

    const onDeleteRole = useCallback(
        async (roleInfo: RoleToDel) => {
            if (!canDeleteRole) return;

            const { data } = await getUsableRole({
                variables: {
                    branchId: Number(branchId),
                    roleId: roleInfo.roleId,
                },
            });

            const isUsableRoleFromData = data?.usableRole ?? false;

            setIsUsableRol(isUsableRoleFromData);

            setDeleteTarget({
                ...roleInfo,
                isUsableRole: isUsableRoleFromData,
            });
        },
        [branchId, canDeleteRole, getUsableRole]
    );

    const [deleteOrRestoreCustomRole] = useDeleteOrRestoreCustomRoleMutation({
        context: {
            errorType: "local",
        },
        fetchPolicy: "network-only",
    });

    const onSubmitDeleteHandler = useCallback(
        async (roleId: number) => {
            if (!canDeleteRole) return;

            await deleteOrRestoreCustomRole({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    roleId: roleId,
                    deleted: true,
                },
            });

            await refetchRole();

            setDeleteTarget(null);
        },
        [refetchRole, branchId, canDeleteRole, currentCompanyId, deleteOrRestoreCustomRole]
    );

    const onCloneRole = useCallback(
        (roleInfo: RoleToClone) => {
            if (!canCloneRole) return;
            setCloneTarget(roleInfo);
        },
        [canCloneRole]
    );

    const onSubmitCloneHandler = useCallback(
        async (roleInfo: RoleToClone) => {
            if (!canCloneRole || !branchId) return;

            setCloneTarget(null);
            navigate(`/branch/role-management/${branchId}/roles/${roleInfo.roleId}/clone`);
        },
        [branchId, canCloneRole, navigate]
    );

    return {
        roleData,
        roleDataLoading,
        refetchRole,
        currentPage,
        pageCount,
        totalItems,
        firstItemIndex,
        lastItemIndex,
        handlePageChange,

        onDeleteRole,
        deleteTarget,
        setDeleteTarget,
        onSubmitDeleteHandler,
        isUsableRole,
        onCloneRole,
        cloneTarget,
        setCloneTarget,
        onSubmitCloneHandler,
    };
};

export default useRoleManagement;
